import Popover from "@mui/material/Popover";
import BinaryStateButton from "components/BinaryStateButton";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import IconButton from "components/IconButton";
import PropTypes from "prop-types";
import { useState } from "react";
import "./PageHeader.scss";

const PageHeader = ({
    actions,
    additionalActions,
    headline,
    title,
    titleImage,
    titleIcon,
    titleAction,
    subtitle,
    children,
    className,
    backButtonLink,
    showImage,
}) => {
    const [actionsPopoverEl, setActionsPopoverEl] = useState(null);
    const hasImage = !!showImage;

    return (
        <div className={`page-header ${className || ""}`}>
            <div className="header">
                {backButtonLink ? (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <div className="page-header__back">
                            <Icon
                                icon={IconCollection.chevronLeft}
                                className="page-header__back-icon"
                            />
                            {backButtonLink?.label}
                        </div>

                        <span className="headline text-dark-grey text-sm">{headline}</span>
                    </div>
                ) : (
                    <span className="headline text-dark-grey text-sm">{headline}</span>
                )}
                {(additionalActions || actions) && (
                    <div className="page-header__actions" data-testid="pageHeaderActions">
                        {additionalActions}
                        {actions && (
                            <>
                                <BinaryStateButton
                                    trueStateIcon={IconCollection.dotsVertical}
                                    falseStateIcon={IconCollection.dotsVertical}
                                    trueStateColor="primary"
                                    state={Boolean(actionsPopoverEl)}
                                    onClick={(event) => {
                                        setActionsPopoverEl(event.target);
                                    }}
                                />
                                <Popover
                                    open={Boolean(actionsPopoverEl)}
                                    anchorEl={actionsPopoverEl}
                                    onClose={() => {
                                        setActionsPopoverEl(null);
                                    }}
                                    anchorOrigin={{
                                        vertical: "bottom",
                                        horizontal: "right",
                                    }}
                                    transformOrigin={{
                                        vertical: "top",
                                        horizontal: "right",
                                    }}
                                >
                                    {actions}
                                </Popover>
                            </>
                        )}
                    </div>
                )}
            </div>
            <div className="body">
                {hasImage &&
                    (titleIcon ? (
                        typeof titleIcon === "string" ? (
                            <div className={"page-header__title-icon"}>
                                <div
                                    className="page-header__title-icon-placeholder"
                                    style={{ backgroundImage: `url(${titleIcon})` }}
                                />
                            </div>
                        ) : (
                            <Icon className="page-header__title-icon" icon={titleIcon} />
                        )
                    ) : (
                        <div
                            className={`page-header__title-image${
                                titleImage ? "" : " page-header__title-image--no-image"
                            }`}
                        >
                            {typeof titleImage !== "string" && titleImage}
                            {typeof titleImage === "string" && (
                                <div
                                    className="page-header__title-image-placeholder"
                                    style={{ backgroundImage: `url(${titleImage})` }}
                                />
                            )}
                        </div>
                    ))}
                {(title || subtitle) && (
                    <div
                        className={`${
                            hasImage ? "page-header__title-headersImage " : ""
                        } page-header__title-headers text-semi-bold`}
                    >
                        {title && (
                            <h1
                                className={`page-header__title leading-none ${
                                    titleAction ? `page-header__titleWithAction` : ""
                                }`}
                            >
                                <div className="page-header__title-text">{title}</div>
                                {titleAction ? (
                                    <div className="page-header__title-action">{titleAction}</div>
                                ) : null}
                            </h1>
                        )}
                        {subtitle && <div className="page-header__subtitle">{subtitle}</div>}
                    </div>
                )}
            </div>
            <div className="footer">{children}</div>
        </div>
    );
};

PageHeader.defaultProps = {
    actions: null,
    additionalActions: null,
    headline: null,
    title: null,
    titleImage: null,
    titleIcon: null,
    titleAction: null,
    subtitle: null,
    children: null,
    className: "",
    backButtonLink: null,
    showImage: true,
};

PageHeader.propTypes = {
    actions: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    additionalActions: PropTypes.any,
    headline: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    title: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    titleImage: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    titleIcon: PropTypes.elementType,
    titleAction: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.func]),
    subtitle: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    children: PropTypes.any,
    className: PropTypes.string,
    backButtonLink: PropTypes.oneOfType([PropTypes.object]),
    showImage: PropTypes.bool,
};

PageHeader.TitleButton = ({ icon, onClick, disabled }) => (
    <button
        className="page-header__title-button"
        type="button"
        disabled={disabled}
        onClick={onClick}
    >
        <Icon className="text-dark-grey" icon={icon} />
    </button>
);
PageHeader.TitleButton.defaultProps = {
    icon: IconCollection.pencilOutline,
    onClick: null,
    disabled: false,
};
PageHeader.TitleButton.propTypes = {
    icon: PropTypes.elementType,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
};

PageHeader.ActionButton = ({ icon, onClick, active, className }) => (
    <IconButton
        className={`${className} page-header__action-button ${
            active ? " page-header__action-button--active" : ""
        }`}
        onClick={onClick}
        icon={icon}
    />
);
PageHeader.ActionButton.defaultProps = {
    icon: IconCollection.informationOutline,
    onClick: null,
    active: null,
    className: "",
};
PageHeader.ActionButton.propTypes = {
    icon: PropTypes.elementType,
    onClick: PropTypes.func,
    active: PropTypes.bool,
    className: PropTypes.string,
};

PageHeader.EditForm = ({ children }) => <div className="page-header__edit-form">{children}</div>;
PageHeader.EditForm.defaultProps = {
    children: null,
};
PageHeader.EditForm.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};

export default PageHeader;
